import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logoutAccount } from "../slice/accountSlice";
import { useNavigate, Link, Outlet, useLocation } from "react-router-dom";
import { useDarkMode } from "../hooks/useDarkMode";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaBars,
  FaTimes,
  FaUser,
  FaHome,
  FaDollarSign,
  FaCog,
  FaLanguage,
  FaIdCard,
  FaFileAlt,
  FaBell,
  FaQuestionCircle,
  FaSun,
  FaMoon,
  FaChevronRight,
  FaPlus,
  FaSearch,
  FaExternalLinkAlt,
  FaEdit,
  FaTrash,
  FaChartBar,
  FaMagic,
} from "react-icons/fa";
import { useTranslation } from "../context/useTranslation";
import axiosInstance from "../apis/axiosConfig";
import { getNotificationsApi } from "../apis/notifications.api";
import { generateBlogApi } from "../apis/blog.api";
import { checkSeoEligibilityApi } from "../apis/payments.api";
import Chatbot from "./Chatbot";
import { useUserLocation } from '../hooks/useUserLocation';

const Dashboard = () => {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const hasFetchedInitialData = useRef(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Helper function to format last scanned time
  const formatLastScannedTime = (dateString) => {
    if (!dateString) return "No Scan Yet";

    try {
      const scanDate = new Date(dateString);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      // Format time as HH:MM AM/PM
      const timeFormatter = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      const time = timeFormatter.format(scanDate);

      // Check if today
      if (scanDate.toDateString() === today.toDateString()) {
        return `Today at ${time}`;
      }

      // Check if yesterday
      if (scanDate.toDateString() === yesterday.toDateString()) {
        return `Yesterday at ${time}`;
      }

      // Otherwise show full date and time
      const dateFormatter = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      const date = dateFormatter.format(scanDate);
      return `${date} at ${time}`;
    } catch (err) {
      return "No Scan Yet";
    }
  };

  // location usage
  // location usage - reserved for future SEO analysis
  useUserLocation(); // Initialize for future use

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [account, setAccount] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [url, setUrl] = useState("");
  const [keyword, setKeyword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [showFreeTrialModal, setShowFreeTrialModal] = useState(false);
  const [blogGenerated, setBlogGenerated] = useState(false);
  const [activeProjectsMap, setActiveProjectsMap] = useState(() => {
    try {
      const raw = localStorage.getItem('activeProjects');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // Subscribe to Redux account state directly
  const reduxAccount = useSelector(state => state.account?.account);

  // Load account from Redux or localStorage
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (reduxAccount) {
      // Use Redux account if available (up-to-date after login)
      const fullName = reduxAccount.name || `${reduxAccount.firstname || ''} ${reduxAccount.lastname || ''}`.trim() || 'User';
      setAccount({ ...reduxAccount, fullName });
    } else {
      // Fallback to localStorage
      const storedAccount = localStorage.getItem("account") || localStorage.getItem("user");
      if (storedAccount && storedAccount !== "undefined" && storedAccount !== "null") {
        try {
          const parsed = JSON.parse(storedAccount);
          const fullName = parsed.name || `${parsed.firstname || ''} ${parsed.lastname || ''}`.trim() || localStorage.getItem('userName') || 'User';
          setAccount({ ...parsed, fullName });
        } catch {
          setAccount(null);
        }
      } else {
        setAccount(null);
      }
    }
  }, [reduxAccount]);

  // Listen for profile updates from EditProfile
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const fullName = event.detail.name || `${event.detail.firstname || ''} ${event.detail.lastname || ''}`.trim();
      setAccount(prev => ({ ...prev, ...event.detail, fullName }));
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-dismiss message after 3.5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Read persisted blog generated flag on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('blog_generated');
    if (stored === 'true') setBlogGenerated(true);
  }, []);

  // Listen to active projects map changes (set by ProjectStatus via localStorage)
  useEffect(() => {
    const loadActive = () => {
      try {
        const raw = localStorage.getItem('activeProjects');
        setActiveProjectsMap(raw ? JSON.parse(raw) : {});
      } catch {
        setActiveProjectsMap({});
      }
    };
    loadActive();

    const onStorage = (e) => {
      if (e.key === 'activeProjects') loadActive();
    };
    const onActiveProjectsUpdated = (e) => {
      if (e?.detail && typeof e.detail === 'object') {
        setActiveProjectsMap(e.detail);
      } else {
        loadActive();
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('activeProjectsUpdated', onActiveProjectsUpdated);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('activeProjectsUpdated', onActiveProjectsUpdated);
    };
  }, []);

  const handleLogout = () => {
    // Clear all authentication and account data
    localStorage.removeItem("account");
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("uid");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("pending2FA");
    localStorage.removeItem("activeProjects");
    dispatch(logoutAccount());
    navigate("/signin");
  };

  // Fetch projects
  const loadProjects = useCallback(async () => {
    // Check authentication first
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    setLoadingProjects(true);
    try {
      const res = await axiosInstance.get('/projects/list/');
      let projectsList = res.data?.projects || res.data || [];

      // Fetch last_scanned time for each project from project_extra_info endpoint
      const projectsWithScanTime = await Promise.all(
        projectsList.map(async (project) => {
          try {
            const extraRes = await axiosInstance.get(`/project/${project.project_id}/extra-info/`);
            return {
              ...project,
              last_scanned: extraRes.data?.last_scanned || null,
            };
          } catch (err) {
            // If endpoint fails, just return project without last_scanned
            console.warn(`Failed to fetch extra info for project ${project.project_id}:`, err);
            return project;
          }
        })
      );

      setProjects(projectsWithScanTime);
    } catch (err) {

      // Check if error is due to 2FA not verified
      if (err.response?.status === 403 && err.response?.data?.error?.includes?.('2FA')) {
        navigate('/login-2fa', { state: { mode: 'login', email: account?.email } });
        return;
      }

      // Handle authentication errors silently (axios interceptor will handle redirects)
      if (err.response?.status === 401 || err.response?.status === 403) {
      }
    } finally {
      setLoadingProjects(false);
    }
  }, [navigate, account?.email]);

  // Fetch notification count
  const loadNotificationCount = useCallback(async () => {
    // Check authentication first
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      const res = await getNotificationsApi();
      const data = res?.data?.notifications ?? res?.data ?? [];
      if (Array.isArray(data)) {
        const count = data.filter((n) => !n.is_read).length;
        setUnreadNotifications(count);
      }
    } catch (err) {
      // Handle token expiration (403 with Firebase token error)
      if (err?.response?.status === 403 && err?.response?.data?.detail?.includes('Firebase token')) {
        try {
          const { refreshToken } = await import('../utils/tokenManagement');
          const newToken = await refreshToken();
          if (newToken) {
            // Retry loading notifications
            return loadNotificationCount();
          }
        } catch (refreshErr) {
        }
        return;
      }

      // Handle silently - axios interceptor will handle auth errors
    }
  }, []);

  useEffect(() => {
    // Prevent duplicate calls in StrictMode
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;

    // Check authentication first
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/signin');
      return;
    }

    // Check 2FA status on dashboard load
    const check2FAStatus = async () => {
      try {
        const { get2FAStatusApi } = await import('../apis/payments.api');
        const twoFAStatus = await get2FAStatusApi();

        // If 2FA is enabled but not verified, redirect to 2FA verification
        if (twoFAStatus.data?.two_factor_enabled && !twoFAStatus.data?.is_verified) {
          navigate('/login-2fa', { state: { mode: 'login', email: account?.email } });
          return;
        }
      } catch (err) {
        // Continue anyway if it's not a critical auth error
        if (err.response?.status === 401) {
          navigate('/signin');
          return;
        }
      }

      // Load projects after 2FA check passes
      loadProjects();
    };

    check2FAStatus();
  }, [loadProjects, navigate, account?.email]);

  // Poll notification count every 30s
  useEffect(() => {
    loadNotificationCount();
    const interval = setInterval(loadNotificationCount, 30000);
    return () => clearInterval(interval);
  }, [loadNotificationCount]);

  const handleSaveProject = async (e) => {
    e.preventDefault();

    if (!projectName.trim()) {
      setMessage({ type: "error", text: "Please enter a project name" });
      return;
    }
    if (!url.trim()) {
      setMessage({ type: "error", text: "Please enter a valid URL" });
      return;
    }
    if (!keyword.trim()) {
      setMessage({ type: "error", text: "Please enter a keyword" });
      return;
    }

    const normalizeUrl = (value) => {
      const trimmed = value.trim();
      if (!trimmed) return trimmed;
      if (/^https?:\/\//i.test(trimmed)) return trimmed;
      return `https://${trimmed}`;
    };

    setSubmitting(true);
    setMessage(null);

    const normalizedUrl = normalizeUrl(url);

    const payload = {
      projectName: projectName.trim(),
      url: normalizedUrl,
      domain_url: normalizedUrl,  // Send both field names in case backend expects this
      keyword: keyword.trim(),
    };

    try {
      if (editingProjectId) {
        await axiosInstance.put(`/projects/edit/${editingProjectId}/`, payload);
        setMessage({ type: "success", text: "Project updated successfully" });
      } else {
        await axiosInstance.post("/projects/", payload);
        setMessage({ type: "success", text: "Project created successfully" });
      }

      setProjectName("");
      setUrl("");
      setKeyword("");
      setEditingProjectId(null);
      setCreateModalOpen(false);
      await loadProjects();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || err.response?.data?.detail || "Failed to save project",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    const id = projectId;
    if (!id) return;
    const confirmDelete = window.confirm("Delete this project?");
    if (!confirmDelete) return;
    try {
      await axiosInstance.delete(`/projects/delete/${id}/`);
      setMessage({ type: "success", text: "Project deleted successfully" });
      await loadProjects();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || err.response?.data?.detail || "Failed to delete project" });
    }
  };

  const handleOpenCreate = () => {
    setEditingProjectId(null);
    setProjectName("");
    setUrl("");
    setKeyword("");
    setCreateModalOpen(true);
  };

  const handleOpenEdit = (p) => {
    setEditingProjectId(p.project_id || p.id);
    setProjectName(p.project_name || p.domain_name || "");
    setUrl(p.domain_url || p.url || "");
    setKeyword(p.keyword || "");
    setCreateModalOpen(true);
  };

  // Sidebar menu items
  const menuItems = [
    { name: t("dashboard") || "Dashboard", icon: <FaHome />, path: "/dashboard" },
    { name: t("profile") || "Profile", icon: <FaUser />, path: "/dashboard/profile" },
    { name: t("accountInfo") || "Account", icon: <FaIdCard />, path: "/dashboard/account-info" },
    { name: t("reportsData") || "Reports", icon: <FaFileAlt />, path: "/dashboard/reports-data" },
    { name: t("pricing") || "Pricing", icon: <FaDollarSign />, path: "/dashboard/pricing" },
    { name: t("settings") || "Settings", icon: <FaCog />, path: "/dashboard/settings" },
    { name: t("language") || "Language", icon: <FaLanguage />, path: "/dashboard/language" },
    { name: t("notifications") || "Notifications", icon: <FaBell />, path: "/dashboard/notifications" },
    { name: t("needHelp") || "Support", icon: <FaQuestionCircle />, path: "/dashboard/need" },
  ];

  return (
    <div className={`flex min-h-screen ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`}>
      {/* Modern Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full z-40 transition-all duration-300 overflow-y-auto w-72 md:${sidebarCollapsed ? 'w-20' : 'w-64'
          } ${isDarkMode ? 'bg-gray-900 border-r border-gray-800' : 'bg-white border-r border-gray-200'} ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } md:translate-x-0`}
      >
        {/* Logo Section */}
        <div className={`sticky top-0 z-50 flex items-center justify-between border-b ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          {sidebarCollapsed ? (
            <Link to="/dashboard" className="flex items-center justify-center w-full">
              <div className="flex items-center justify-center">
                <div className="flex items-center justify-center">
                  <img
                    src={isDarkMode ? "/Images/ICONSLOGO.png" : "/Images/ICONSLOGO.png"}
                    alt="Logo"
                    className="h-16 p-1.5 object-contain"
                  /></div>
              </div>

            </Link>
          ) : (
            <Link to="/dashboard" className="flex items-center gap-3 flex-1">

              {isDarkMode ? (
                <div className="h-16 flex items-center justify-center">
                  <img
                    src="/Images/LOGO_DARK.png"
                    alt="SEO Logo"
                    className="h-14 object-contain"
                  />
                </div>

              ) : (
                <div className="h-16 flex items-center justify-center">
                  <img
                    src="/Images/LOGO_LIGHT.png"
                    alt="SEO Logo"
                    className="h-14 pl-2 object-contain"
                  />
                </div>
              )}
            </Link>
          )}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={`p-1.5 hidden sm:inline rounded-lg transition ${isDarkMode
                ? 'hover:bg-gray-800 text-gray-400'
                : 'hover:bg-gray-100 text-gray-600'
                }`}
            >
              {sidebarCollapsed ? <FaChevronRight size={16} /> : <FaTimes size={16} />}
            </button>
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className={`p-1.5 md:hidden rounded-lg transition ${isDarkMode
                ? 'hover:bg-gray-800 text-gray-300'
                : 'hover:bg-gray-100 text-gray-700'
                }`}
              aria-label="Close sidebar"
            >
              <FaTimes size={18} />
            </button>
          </div>
        </div>

        {/* User Info - show full info when expanded, compact initial when collapsed */}
        {!sidebarCollapsed ? (
          <div className={`px-4 py-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
            {account?.profileImage ? (
              <img
                src={account.profileImage}
                alt="Profile"
                className="w-12 h-12 rounded-full border-2 border-[#008300] mx-auto mb-3 object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full border-2 border-[#008300] mx-auto mb-3 bg-gradient-to-br from-[#008300] to-[#006600] flex items-center justify-center">
                <span className="text-xl font-bold text-white">
                  {(account?.fullName || account?.firstname || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <h3 className={`text-sm font-bold text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {account?.fullName || account?.firstname || 'User'}
            </h3>
            <p className={`text-xs text-center break-all mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {account?.email || ''}
            </p>
          </div>
        ) : (
          <div className={`flex items-center justify-center py-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="w-10 h-10 rounded-full bg-[#008300] text-white font-semibold flex items-center justify-center">
              {(account?.fullName || account?.firstname || 'U').charAt(0).toUpperCase()}
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav className={`flex-1 px-2 py-6 ${sidebarCollapsed ? 'flex flex-col items-center justify-start space-y-4' : 'space-y-2'}`}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsMobileSidebarOpen(false)}
                title={sidebarCollapsed ? item.name : ''}
                className={`flex items-center ${sidebarCollapsed ? 'justify-center py-3 px-0' : 'gap-3 px-4 py-3'} rounded-lg transition duration-200 ${isActive
                  ? isDarkMode
                    ? 'bg-[#008300]/20 text-[#00ff00] border-l-4 border-[#008300]'
                    : 'bg-[#008300]/10 text-[#008300] border-l-4 border-[#008300]'
                  : isDarkMode
                    ? 'text-gray-400 hover:bg-gray-800'
                    : 'text-gray-600 hover:bg-gray-100'
                  }`}
              >
                <span className={`text-lg ${sidebarCollapsed ? '' : ''}`}>{item.icon}</span>
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1 text-sm font-medium">{item.name}</span>
                    {item.path === "/dashboard/notifications" && unreadNotifications > 0 && (
                      <span className="px-2 py-1 text-xs font-bold rounded-full bg-red-500 text-white">
                        {unreadNotifications}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout Button */}
        {!sidebarCollapsed && (
          <div className={`p-4 border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
            <button
              onClick={handleLogout}
              className={`w-full px-4 py-2 rounded-lg font-medium text-sm transition ${isDarkMode
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-red-50 hover:bg-red-100 text-red-600'
                }`}
            >
              Logout
            </button>
          </div>
        )}
      </aside>

      {/* Mobile Backdrop */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main Content Area */}
      <main className={`flex-1 transition-all duration-300 ml-0 md:${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        {/* Top Navbar */}
        <nav
          className={`sticky top-0 z-50 border-b ${isDarkMode
            ? 'bg-gray-900 border-gray-800'
            : 'bg-white border-gray-200'
            }`}
        >
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4 flex-1">
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className={`md:hidden p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                  }`}
              >
                <FaBars size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-600'} />
              </button>
              <div>
                <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t("welcome")}, {account?.fullName || account?.firstname || 'User'}! 👋
                </h1>
                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg transition ${isDarkMode
                  ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {isDarkMode ? <FaSun size={18} /> : <FaMoon size={18} />}
              </button>

              {/* Mobile Logout */}
              {/* <button
                onClick={handleLogout}
                className={`lg:hidden p-2 rounded-lg transition ${isDarkMode
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-red-50 hover:bg-red-100 text-red-600'
                  }`}
              >
                <FaTimes size={18} />
              </button> */}
            </div>
          </div>
        </nav>

        {/* Page Content */}
        <div className="overflow-auto">
          {location.pathname === "/dashboard" ? (
            <div className={`min-h-[calc(100vh-120px)] ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`}>
              <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  <div className={`p-6 rounded-xl border-l-4 border-[#008300] ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Projects</p>
                    <p className={`text-2xl font-bold mt-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{projects.length}</p>
                  </div>
                  <div className={`p-6 rounded-xl border-l-4 border-blue-500 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Notifications</p>
                    <p className={`text-2xl font-bold mt-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{unreadNotifications}</p>
                  </div>
                  {/* <div className={`p-6 rounded-xl border-l-4 border-purple-500 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Active Status</p>
                    <p className={`text-2xl font-bold mt-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{Object.values(activeProjectsMap).filter(v => v).length}</p>
                  </div> */}
                  <div className={`p-6 rounded-xl border-l-4 border-yellow-500 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Last Updated</p>
                    <p className={`text-sm font-bold mt-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {projects.length > 0 ? formatLastScannedTime(projects[0]?.last_scanned) : 'No projects'}
                    </p>
                  </div>
                </div>

                {/* Projects Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Projects</h2>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manage and monitor your website SEO</p>
                  </div>
                  <button
                    onClick={handleOpenCreate}
                    className="flex items-center gap-2 px-6 py-3 bg-[#008300] hover:bg-[#006600] text-white rounded-lg font-medium transition"
                  >
                    <FaPlus /> New Project
                  </button>
                </div>

                {/* Alert Messages */}
                {message && (
                  <div
                    className={`mb-6 p-4 rounded-lg border text-sm ${message.type === "success"
                      ? isDarkMode
                        ? 'bg-green-900/30 text-green-300 border-green-700'
                        : 'bg-green-50 text-green-800 border-green-200'
                      : isDarkMode
                        ? 'bg-red-900/30 text-red-300 border-red-700'
                        : 'bg-red-50 text-red-800 border-red-200'
                      }`}
                  >
                    {message.text}
                  </div>
                )}

                {/* Projects List */}
                {loadingProjects ? (
                  <div className={`flex items-center justify-center py-16 ${isDarkMode ? 'bg-gray-900' : 'bg-white'} rounded-lg`}>
                    <div className="text-center">
                      <div className={`inline-block w-12 h-12 border-4 rounded-full border-t-[#008300] animate-spin mb-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}></div>
                      <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading projects...</p>
                    </div>
                  </div>
                ) : projects.length === 0 ? (
                  <div className={`text-center py-16 rounded-lg border-2 border-dashed ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-300'}`}>
                    <FaSearch className={`text-4xl mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                    <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No Projects Yet</h3>
                    <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Create your first project to start analyzing SEO performance</p>
                    <button
                      onClick={handleOpenCreate}
                      className="inline-flex items-center gap-2 px-6 py-2 bg-[#008300] hover:bg-[#006600] text-white rounded-lg font-medium transition"
                    >
                      <FaPlus /> Create Project
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {projects.map((p) => (
                      <div
                        key={p.project_id || p.id}
                        className={`rounded-lg border transition hover:shadow-lg ${isDarkMode
                          ? 'bg-gray-900 border-gray-800 hover:border-gray-700'
                          : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <div className="p-6">
                          {/* Header Row */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-2xl">🌐</span>
                                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {p.project_name || p.domain_name}
                                </h3>
                                {activeProjectsMap[p.project_id || p.id] && (
                                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full">Active</span>
                                )}
                              </div>
                              <a
                                href={p.domain_url || p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-[#008300] hover:text-[#006600] transition"
                              >
                                {p.domain_url || p.url}
                                <FaExternalLinkAlt size={12} />
                              </a>
                            </div>
                          </div>

                          {/* Info Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 pb-4 border-t border-b" style={{ borderTopColor: isDarkMode ? '#2d3748' : '#e5e7eb', borderBottomColor: isDarkMode ? '#2d3748' : '#e5e7eb' }}>
                            <div>
                              <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Keyword</p>
                              <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{p.keyword || 'N/A'}</p>
                            </div>
                            <div>
                              <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Credits</p>
                              <p className={`font-semibold text-yellow-500`}>⚡ {p.remaining_credits ?? p.remainingCredits ?? p.credits ?? '-'}</p>
                            </div>
                            <div>
                              <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Last Scanned</p>
                              <p className={`font-semibold text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{formatLastScannedTime(p.last_scanned || p.lastScanned)}</p>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-3 flex-wrap">
                            <button
                              onClick={() => {
                                const projectId = p.project_id || p.id;
                                navigate('/dashboard/seo-analyzer', {
                                  state: {
                                    projectId,
                                    projectName: p.project_name || p.domain_name,
                                    domainUrl: p.domain_url || p.url,
                                    keyword: p.keyword,
                                  },
                                });
                              }}
                              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-[#008300] hover:bg-[#006600] text-white rounded-lg font-medium transition"
                            >
                              <FaChartBar size={16} />
                              <span className="hidden md:inline">Analyze</span>
                            </button>

                            <button
                              onClick={() => {
                                const projectId = p.project_id || p.id;
                                navigate('/dashboard/seo-history', {
                                  state: {
                                    projectId,
                                    projectName: p.project_name || p.domain_name,
                                    domainUrl: p.domain_url || p.url,
                                  },
                                });
                              }}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${isDarkMode
                                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                              <FaFileAlt size={16} />
                              <span className="hidden md:inline">History</span>
                            </button>

                            <button
                              onClick={() => handleOpenEdit(p)}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${isDarkMode
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                                }`}
                            >
                              <FaEdit size={16} />
                              <span className="hidden md:inline">Edit</span>
                            </button>

                            <button
                              onClick={async () => {
                                const projectId = p.project_id || p.id;
                                if (blogGenerated) {
                                  // Check if user has a subscription now
                                  try {
                                    const res = await checkSeoEligibilityApi({ project_id: projectId });
                                    if (res.data.has_subscription) {
                                      // User has purchased a plan, allow them to generate
                                      navigate(`/dashboard/blog-generator/${projectId}`, {
                                        state: {
                                          projectId,
                                          projectName: p.project_name || p.domain_name,
                                          domainUrl: p.domain_url || p.url,
                                        },
                                      });
                                      return;
                                    }
                                  } catch (err) {
                                    console.error('Failed to check subscription:', err);
                                  }
                                  // Still no subscription, show the free trial modal
                                  setShowFreeTrialModal(true);
                                  return;
                                }
                                navigate(`/dashboard/blog-generator/${projectId}`, {
                                  state: {
                                    projectId,
                                    projectName: p.project_name || p.domain_name,
                                    domainUrl: p.domain_url || p.url,
                                  },
                                });
                              }}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${isDarkMode
                                ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                : 'bg-purple-100 hover:bg-purple-200 text-purple-700'
                                }`}
                            >
                              <FaMagic size={16} />
                              <span className="hidden md:inline">Blog Generator</span>
                            </button>

                            <button
                              onClick={() => handleDeleteProject(p.project_id || p.id)}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${isDarkMode
                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                : 'bg-red-100 hover:bg-red-200 text-red-700'
                                }`}
                            >
                              <FaTrash size={16} />
                              <span className="hidden md:inline">Delete</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}>
              <Outlet />
            </div>
          )}

          {/* Create/Edit Project Modal */}
          {createModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className={`relative w-full max-w-lg rounded-xl shadow-2xl p-6 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
                {/* Close Button */}
                <button
                  onClick={() => { setEditingProjectId(null); setCreateModalOpen(false); }}
                  className="absolute top-4 right-4 p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-lg transition"
                >
                  <FaTimes />
                </button>

                <h3 className={`text-2xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {editingProjectId ? "Edit Project" : "Create New Project"}
                </h3>
                <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {editingProjectId ? "Update your project details" : "Add a new project to start monitoring"}
                </p>

                {message && (
                  <div
                    className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success'
                      ? isDarkMode
                        ? 'bg-green-900/30 text-green-300'
                        : 'bg-green-50 text-green-800'
                      : isDarkMode
                        ? 'bg-red-900/30 text-red-300'
                        : 'bg-red-50 text-red-800'
                      }`}
                  >
                    {message.text}
                  </div>
                )}

                <form onSubmit={handleSaveProject} className="space-y-4">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Project Name
                    </label>
                    <input
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="My Website SEO"
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#008300] ${isDarkMode
                        ? 'bg-gray-800 border-gray-700 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Website URL
                    </label>
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://example.com"
                      disabled={editingProjectId}
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#008300] ${isDarkMode
                        ? 'bg-gray-800 border-gray-700 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        } ${editingProjectId ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    {editingProjectId && (
                      <p className="text-xs text-red-500 mt-1">⚠️ URL cannot be modified</p>
                    )}
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Target Keyword
                    </label>
                    <input
                      type="text"
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      placeholder="e.g., best seo tools"
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#008300] ${isDarkMode
                        ? 'bg-gray-800 border-gray-700 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => { setEditingProjectId(null); setCreateModalOpen(false); }}
                      className={`px-6 py-2 rounded-lg font-medium transition ${isDarkMode
                        ? 'bg-gray-800 hover:bg-gray-700 text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                        }`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className={`px-6 py-2 rounded-lg font-medium text-white transition ${submitting
                        ? 'opacity-60 cursor-not-allowed'
                        : 'bg-[#008300] hover:bg-[#006600]'
                        }`}
                    >
                      {submitting ? 'Saving...' : editingProjectId ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Free Trial Modal */}
          {showFreeTrialModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className={`w-full max-w-md mx-4 p-8 rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                <h2 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>You have used your Free Trial</h2>
                <p className={`mb-6 text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  If you want to generate a blog again, you need to purchase a subscription plan to continue.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowFreeTrialModal(false);
                      navigate('/dashboard/pricing');
                    }}
                    className="flex-1 py-3 rounded-xl font-bold text-white bg-green-600 hover:bg-green-700 transition-colors shadow-md"
                  >
                    Subscription Plan
                  </button>
                  <button
                    onClick={() => {
                      setShowFreeTrialModal(false);
                      navigate('/dashboard');
                    }}
                    className="flex-1 py-3 rounded-xl font-bold text-white bg-green-600 hover:bg-green-700 transition-colors shadow-md"
                  >
                    Dashboard
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </main>

      {/* Chatbot */}
      <Chatbot />
    </div>
  );
};

export default Dashboard;
